import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { UserNotificationService } from '@6grain/masofa-monolith-api-client/notification/api/userNotification.service';
import { NotificationDeliveryProfileResponse } from '@6grain/masofa-monolith-api-client/notification/model/notificationDeliveryProfileResponse';
import { AdminUserInboxItemResponse } from '@6grain/masofa-monolith-api-client/notification/model/adminUserInboxItemResponse';
import { NotificationDeliveryResponse } from '@6grain/masofa-monolith-api-client/notification/model/notificationDeliveryResponse';
import { NotificationEscalationStep } from '@6grain/masofa-monolith-api-client/notification/model/notificationEscalationStep';
import { NotificationRecipientSelector } from '@6grain/masofa-monolith-api-client/notification/model/notificationRecipientSelector';
import { NotificationRuleResponse } from '@6grain/masofa-monolith-api-client/notification/model/notificationRuleResponse';
import { NotificationTemplateResponse } from '@6grain/masofa-monolith-api-client/notification/model/notificationTemplateResponse';
import { SaveNotificationTemplateRequest } from '@6grain/masofa-monolith-api-client/notification/model/saveNotificationTemplateRequest';
import { SaveNotificationRuleRequest } from '@6grain/masofa-monolith-api-client/notification/model/saveNotificationRuleRequest';
import { SaveNotificationEmailTransportRequest } from '@6grain/masofa-monolith-api-client/notification/model/saveNotificationEmailTransportRequest';
import { UserNotificationEmailTransportResponse } from '@6grain/masofa-monolith-api-client/notification/model/userNotificationEmailTransportResponse';
import { UserInboxItemResponse } from '@6grain/masofa-monolith-api-client/notification/model/userInboxItemResponse';
import { UserNotificationPreferenceResponse } from '@6grain/masofa-monolith-api-client/notification/model/userNotificationPreferenceResponse';
import {
  toRecipientSelectorDraft,
  toRecipientSelectorSave,
} from '@app/pages/admin/notification-recipient-selector.util';

export interface NotifiableEventDto {
  key: string;
  displayName: string;
  description: string;
  payloadFields: string[];
  targetEntityType?: string | null;
}

export interface UserNotificationChannelDto {
  key: string;
  displayName: string;
  enabled: boolean;
  hasWebhook: boolean;
}

export interface UserNotificationEmailTransportDto {
  host: string;
  port: number;
  user?: string | null;
  from: string;
  useSsl: boolean;
  validateServerCertificate: boolean;
  passwordConfigured: boolean;
}

export interface NotificationDeliveryProfileDto {
  id?: string | null;
  isDefault: boolean;
  userId?: string | null;
  email?: string | null;
  maxEmailsPerHour: number;
  maxRetryAttempts: number;
  digestEnabled: boolean;
  digestHourUtc: number;
  emailEnabled: boolean;
  inAppEnabled: boolean;
}

export interface UserNotificationSettingsDto {
  maxEmailsPerHour: number;
  maxRetryAttempts: number;
  digestEnabled: boolean;
  digestHourUtc: number;
  channels: UserNotificationChannelDto[];
  emailTransport?: UserNotificationEmailTransportDto;
}

export interface NotificationTemplateDto {
  id: string;
  name: string;
  title: Record<string, string>;
  body: Record<string, string>;
  includeAttachments: boolean;
  includeReportPreview: boolean;
  deepLinkPattern?: string | null;
  isRetired: boolean;
}

export interface NotificationRecipientSelectorDto {
  kind: string;
  entityId?: string | null;
  roleName?: string | null;
  emailAddress?: string | null;
}

export interface NotificationEscalationStepDto {
  afterMinutes: number;
  channelKeys: string[];
  recipients: NotificationRecipientSelectorDto[];
}

export interface NotificationRuleDto {
  id: string;
  name: string;
  eventKey: string;
  sourceProcessDefinitionKey?: string | null;
  priority: string;
  templateId: string;
  isActive: boolean;
  recipients: NotificationRecipientSelectorDto[];
  channels: string[];
  escalation: NotificationEscalationStepDto[];
}

export interface NotificationDeliveryDto {
  id: string;
  occurrenceId: string;
  ruleId: string;
  eventKey: string;
  recipientUserId: string;
  recipientEmail?: string | null;
  channelKey: string;
  priority: string;
  renderedTitle: string;
  renderedBody: string;
  deepLink?: string | null;
  outcome: string;
  reason?: string | null;
  attemptNumber: number;
  createAt: string;
  isTest: boolean;
  isDigest: boolean;
  isEscalation: boolean;
  isCapSkip: boolean;
  targetEntityType?: string | null;
  targetEntityId?: string | null;
}

export interface UserInboxItemDto {
  id: string;
  eventKey: string;
  priority: string;
  title: string;
  body: string;
  deepLink?: string | null;
  readAt?: string | null;
  createAt: string;
}

export interface AdminInboxItemDto {
  id: string;
  recipientUserId: string;
  ruleId: string;
  occurrenceId: string;
  eventKey: string;
  priority: string;
  title: string;
  body: string;
  deepLink?: string | null;
  readAt?: string | null;
  createAt: string;
  isTest: boolean;
}

export interface UserNotificationPreferenceDto {
  userId: string;
  quietHoursStartMinutesUtc?: number | null;
  quietHoursEndMinutesUtc?: number | null;
  optOutEventKeys: string[];
}

@Injectable({ providedIn: 'root' })
export class UserNotificationsApiService {
  private readonly api = inject(UserNotificationService);

  getEvents(): Observable<NotifiableEventDto[]> {
    return this.api.userNotificationsEventsGet().pipe(map((rows) => (rows ?? []).map(toEvent)));
  }

  getSettings(): Observable<UserNotificationSettingsDto> {
    return this.api.userNotificationsSettingsGet().pipe(map(toSettings));
  }

  getDeliveryProfiles(): Observable<NotificationDeliveryProfileDto[]> {
    return this.api.userNotificationsDeliveryProfilesGet().pipe(map((rows) => (rows ?? []).map(toDeliveryProfile)));
  }

  saveDeliveryProfile(body: NotificationDeliveryProfileDto): Observable<NotificationDeliveryProfileDto> {
    return this.api.userNotificationsDeliveryProfilesPut(body).pipe(map(toDeliveryProfile));
  }

  saveEmailTransport(body: SaveNotificationEmailTransportRequest): Observable<UserNotificationEmailTransportDto> {
    return this.api.userNotificationsSettingsEmailTransportPut(body).pipe(map(toEmailTransport));
  }

  getTemplates(): Observable<NotificationTemplateDto[]> {
    return this.api.userNotificationsTemplatesGet().pipe(map((rows) => (rows ?? []).map(toTemplate)));
  }

  createTemplate(body: Omit<NotificationTemplateDto, 'id'>): Observable<NotificationTemplateDto> {
    return this.api.userNotificationsTemplatesPost(toSaveTemplate(body)).pipe(map(toTemplate));
  }

  updateTemplate(id: string, body: Omit<NotificationTemplateDto, 'id'>): Observable<NotificationTemplateDto> {
    return this.api.userNotificationsTemplatesTemplateIdPut(id, toSaveTemplate(body)).pipe(map(toTemplate));
  }

  getRules(): Observable<NotificationRuleDto[]> {
    return this.api.userNotificationsRulesGet().pipe(map((rows) => (rows ?? []).map(toRule)));
  }

  createRule(body: Omit<NotificationRuleDto, 'id'>): Observable<NotificationRuleDto> {
    return this.api.userNotificationsRulesPost(toSaveRule(body)).pipe(map(toRule));
  }

  updateRule(id: string, body: Omit<NotificationRuleDto, 'id'>): Observable<NotificationRuleDto> {
    return this.api.userNotificationsRulesRuleIdPut(id, toSaveRule(body)).pipe(map(toRule));
  }

  deleteRule(id: string): Observable<void> {
    return this.api.userNotificationsRulesRuleIdDelete(id).pipe(map(() => undefined));
  }

  testRule(id: string, recipientUserId?: string): Observable<number> {
    const body = recipientUserId ? { recipientUserId } : {};
    return this.api.userNotificationsRulesRuleIdTestPost(id, body);
  }

  getJournal(filters?: {
    eventKey?: string;
    ruleId?: string;
    outcome?: string;
    isTest?: boolean;
    isDigest?: boolean;
    isEscalation?: boolean;
    isCapSkip?: boolean;
    targetEntityId?: string;
  }): Observable<NotificationDeliveryDto[]> {
    return this.api
      .userNotificationsJournalGet(
        filters?.eventKey,
        filters?.ruleId,
        undefined,
        undefined,
        undefined,
        filters?.outcome,
        filters?.isTest,
        filters?.isDigest,
        filters?.isEscalation,
        filters?.isCapSkip,
        filters?.targetEntityId,
        filters?.targetEntityId ? 200 : undefined,
      )
      .pipe(map((rows) => (rows ?? []).map(toDelivery)));
  }

  getInbox(): Observable<UserInboxItemDto[]> {
    return this.api.userNotificationsInboxGet().pipe(map((rows) => (rows ?? []).map(toInbox)));
  }

  getAllInbox(filters?: {
    eventKey?: string;
    unreadOnly?: boolean;
    isTest?: boolean;
  }): Observable<AdminInboxItemDto[]> {
    return this.api
      .userNotificationsInboxAllGet(filters?.eventKey, undefined, filters?.unreadOnly, filters?.isTest)
      .pipe(map((rows) => (rows ?? []).map(toAdminInbox)));
  }

  markRead(id: string): Observable<void> {
    return this.api.userNotificationsInboxItemIdReadPost(id).pipe(map(() => undefined));
  }

  markAllRead(): Observable<void> {
    return this.api.userNotificationsInboxReadAllPost().pipe(map(() => undefined));
  }

  getPreferences(): Observable<UserNotificationPreferenceDto> {
    return this.api.userNotificationsPreferencesGet().pipe(map(toPreference));
  }

  savePreferences(body: Partial<UserNotificationPreferenceDto>): Observable<UserNotificationPreferenceDto> {
    return this.api
      .userNotificationsPreferencesPut({
        quietHoursStartMinutesUtc: body.quietHoursStartMinutesUtc,
        quietHoursEndMinutesUtc: body.quietHoursEndMinutesUtc,
        optOutEventKeys: body.optOutEventKeys,
      })
      .pipe(map(toPreference));
  }
}

function toEvent(row: Partial<NotifiableEventDto> | Record<string, unknown>): NotifiableEventDto {
  const item = row as NotifiableEventDto;
  return {
    key: item.key ?? '',
    displayName: item.displayName ?? item.key ?? '',
    description: item.description ?? '',
    payloadFields: item.payloadFields ?? [],
    targetEntityType: item.targetEntityType,
  };
}

function toSettings(row: Partial<UserNotificationSettingsDto> | Record<string, unknown>): UserNotificationSettingsDto {
  const item = row as UserNotificationSettingsDto;
  return {
    maxEmailsPerHour: item.maxEmailsPerHour ?? 0,
    maxRetryAttempts: item.maxRetryAttempts ?? 0,
    digestEnabled: !!item.digestEnabled,
    digestHourUtc: item.digestHourUtc ?? 0,
    channels: (item.channels ?? []).map((channel) => ({
      key: channel.key ?? '',
      displayName: channel.displayName ?? channel.key ?? '',
      enabled: !!channel.enabled,
      hasWebhook: !!channel.hasWebhook,
    })),
    emailTransport: toEmailTransport(item.emailTransport),
  };
}

function certificateValidation(
  row: UserNotificationEmailTransportResponse | UserNotificationEmailTransportDto | null | undefined,
): boolean {
  if (row && 'validateServerCertificate' in row)
    return row.validateServerCertificate !== false;
  return true;
}

function toEmailTransport(
  row: UserNotificationEmailTransportResponse | UserNotificationEmailTransportDto | null | undefined,
): UserNotificationEmailTransportDto {
  return {
    host: row?.host ?? '',
    port: row?.port ?? 465,
    user: row?.user,
    from: row?.from ?? '',
    useSsl: row?.useSsl !== false,
    validateServerCertificate: certificateValidation(row),
    passwordConfigured: !!row?.passwordConfigured,
  };
}

function toTemplate(row: NotificationTemplateResponse): NotificationTemplateDto {
  return {
    id: row.id ?? '',
    name: row.name ?? '',
    title: row.title ?? {},
    body: row.body ?? {},
    includeAttachments: !!row.includeAttachments,
    includeReportPreview: !!row.includeReportPreview,
    deepLinkPattern: row.deepLinkPattern,
    isRetired: !!row.isRetired,
  };
}

function toSaveTemplate(body: Omit<NotificationTemplateDto, 'id'>): SaveNotificationTemplateRequest {
  return {
    name: body.name,
    title: body.title,
    body: body.body,
    includeAttachments: body.includeAttachments,
    includeReportPreview: body.includeReportPreview,
    deepLinkPattern: body.deepLinkPattern,
    isRetired: body.isRetired,
  };
}

function toSelector(row: NotificationRecipientSelector): NotificationRecipientSelectorDto {
  const draft = toRecipientSelectorDraft({
    kind: row.kind as unknown as string | number,
    entityId: row.entityId,
    roleName: row.roleName,
    emailAddress: row.emailAddress,
  });
  const saved = toRecipientSelectorSave(draft);
  return {
    kind: String(saved?.kind ?? draft.kind),
    entityId: (saved?.entityId ?? draft.entityId) || null,
    roleName: (saved?.roleName ?? draft.roleName) || null,
    emailAddress: (saved?.emailAddress ?? draft.emailAddress) || null,
  };
}

function toSaveSelector(row: NotificationRecipientSelectorDto): NotificationRecipientSelector {
  const saved = toRecipientSelectorSave({
    kind: row.kind,
    entityId: row.entityId ?? '',
    roleName: row.roleName ?? '',
    emailAddress: row.emailAddress ?? '',
  });
  return {
    kind: (saved?.kind ?? row.kind) as unknown as NotificationRecipientSelector['kind'],
    entityId: saved?.entityId ?? row.entityId,
    roleName: saved?.roleName ?? row.roleName,
    emailAddress: saved?.emailAddress ?? row.emailAddress,
  };
}

function toEscalation(row: NotificationEscalationStep): NotificationEscalationStepDto {
  return {
    afterMinutes: row.afterMinutes ?? 0,
    channelKeys: row.channelKeys ?? [],
    recipients: (row.recipients ?? []).map(toSelector),
  };
}

function toRule(row: NotificationRuleResponse): NotificationRuleDto {
  return {
    id: row.id ?? '',
    name: row.name ?? '',
    eventKey: row.eventKey ?? '',
    sourceProcessDefinitionKey: row.sourceProcessDefinitionKey ?? '',
    priority: row.priority ?? 'Normal',
    templateId: row.templateId ?? '',
    isActive: row.isActive !== false,
    recipients: (row.recipients ?? []).map(toSelector),
    channels: row.channels ?? [],
    escalation: (row.escalation ?? []).map(toEscalation),
  };
}

function toSaveRule(body: Omit<NotificationRuleDto, 'id'>): SaveNotificationRuleRequest {
  return {
    name: body.name,
    eventKey: body.eventKey,
    sourceProcessDefinitionKey: body.sourceProcessDefinitionKey || undefined,
    priority: body.priority,
    templateId: body.templateId,
    isActive: body.isActive,
    recipients: body.recipients.map(toSaveSelector),
    channels: body.channels,
    escalation: body.escalation.map((step) => ({
      afterMinutes: step.afterMinutes,
      channelKeys: step.channelKeys,
      recipients: step.recipients.map(toSaveSelector),
    })),
  } as SaveNotificationRuleRequest;
}

function isEmptyGuid(value: string | null | undefined): boolean {
  return !value || /^0{8}-0{4}-0{4}-0{4}-0{12}$/i.test(value);
}

function toDeliveryProfile(row: NotificationDeliveryProfileResponse): NotificationDeliveryProfileDto {
  return {
    id: row.id,
    isDefault: !!row.isDefault,
    userId: row.userId,
    email: row.email,
    maxEmailsPerHour: row.maxEmailsPerHour ?? 0,
    maxRetryAttempts: row.maxRetryAttempts ?? 0,
    digestEnabled: !!row.digestEnabled,
    digestHourUtc: row.digestHourUtc ?? 0,
    emailEnabled: row.emailEnabled !== false,
    inAppEnabled: row.inAppEnabled !== false,
  };
}

function toDelivery(row: NotificationDeliveryResponse): NotificationDeliveryDto {
  const recipientUserId = row.recipientUserId ?? '';
  return {
    id: row.id ?? '',
    occurrenceId: row.occurrenceId ?? '',
    ruleId: row.ruleId ?? '',
    eventKey: row.eventKey ?? '',
    recipientUserId: isEmptyGuid(recipientUserId) ? '' : recipientUserId,
    recipientEmail: row.recipientEmail ?? null,
    channelKey: row.channelKey ?? '',
    priority: row.priority ?? '',
    renderedTitle: row.renderedTitle ?? '',
    renderedBody: row.renderedBody ?? '',
    deepLink: row.deepLink,
    outcome: row.outcome ?? '',
    reason: row.reason,
    attemptNumber: row.attemptNumber ?? 0,
    createAt: row.createAt ?? '',
    isTest: !!row.isTest,
    isDigest: !!row.isDigest,
    isEscalation: !!row.isEscalation,
    isCapSkip: !!row.isCapSkip,
    targetEntityType: row.targetEntityType,
    targetEntityId: row.targetEntityId,
  };
}

function toInbox(row: UserInboxItemResponse): UserInboxItemDto {
  return {
    id: row.id ?? '',
    eventKey: row.eventKey ?? '',
    priority: row.priority ?? '',
    title: row.title ?? '',
    body: row.body ?? '',
    deepLink: row.deepLink,
    readAt: row.readAt,
    createAt: row.createAt ?? '',
  };
}

function toAdminInbox(row: AdminUserInboxItemResponse): AdminInboxItemDto {
  return {
    id: row.id ?? '',
    recipientUserId: row.recipientUserId ?? '',
    ruleId: row.ruleId ?? '',
    occurrenceId: row.occurrenceId ?? '',
    eventKey: row.eventKey ?? '',
    priority: row.priority ?? '',
    title: row.title ?? '',
    body: row.body ?? '',
    deepLink: row.deepLink,
    readAt: row.readAt,
    createAt: row.createAt ?? '',
    isTest: !!row.isTest,
  };
}

function toPreference(row: UserNotificationPreferenceResponse): UserNotificationPreferenceDto {
  return {
    userId: row.userId ?? '',
    quietHoursStartMinutesUtc: row.quietHoursStartMinutesUtc,
    quietHoursEndMinutesUtc: row.quietHoursEndMinutesUtc,
    optOutEventKeys: row.optOutEventKeys ?? [],
  };
}
